// Reshapes metric results for the "by_entity" chart layout: one entry per
// store/employee/role with every selected metric side by side.

export const entityName = (row) =>
  (row && (row.location || row.employee || row.role)) || null;

// Column header for the entity column, from whichever key the rows carry.
export const entityLabel = (rows) => {
  const first = (rows || []).find((row) => entityName(row));
  if (!first) return "Name";
  if (first.location) return "Store";
  if (first.employee) return "Employee";
  return "Role";
};

// location_data is long format ({location, metric, total}); pivot it to
// [{entity, <metric>: total}] sorted by the first metric, largest first.
export const pivotEntityTotals = (locationData, selectedMetrics) => {
  const metrics = selectedMetrics || [];
  const byEntity = new Map();
  (locationData || []).forEach((row) => {
    const name = entityName(row);
    if (!name || !metrics.includes(row.metric)) return;
    if (!byEntity.has(name)) byEntity.set(name, { entity: name });
    byEntity.get(name)[row.metric] = row.total;
  });
  const sortKey = metrics[0];
  const valueOf = (row) =>
    typeof row[sortKey] === "number" && !isNaN(row[sortKey]) ? row[sortKey] : -Infinity;
  return [...byEntity.values()].sort((a, b) => valueOf(b) - valueOf(a));
};

const hasValue = (value) =>
  value !== null && value !== undefined && !(typeof value === "number" && isNaN(value));

// raw_data rows ({location, date, <metric>...}) grouped per entity and sorted
// by date; with intraday, intraday_data rows ({location, date, qh_num, ...})
// per date and quarter hour. Rows for the same entity and period are merged:
// the employee endpoint returns one row per metric with the other metrics
// zero-filled, so a zero or missing value yields to the other row's value
// (which is also their sum). Returns null when the rows cannot be shown per
// period: no row carries a selected metric, or two rows carry non-zero values
// of one metric for one entity and period (e.g. a role breakdown spanning
// several stores), which would need re-aggregation.
export const pivotEntityPeriods = (rawData, selectedMetrics, { intraday = false } = {}) => {
  const metrics = selectedMetrics || [];
  const byEntity = {};
  const periodsByKey = new Map();
  let hasMetric = false;
  for (const row of rawData || []) {
    const name = entityName(row);
    if (!name || !row.date) continue;
    if (intraday && (row.qh_num === null || row.qh_num === undefined)) continue;
    const key = intraday ? `${name}|${row.date}|${row.qh_num}` : `${name}|${row.date}`;
    let period = periodsByKey.get(key);
    if (!period) {
      period = intraday ? { date: row.date, qh_num: row.qh_num } : { date: row.date };
      metrics.forEach((metric) => {
        period[metric] = undefined;
      });
      periodsByKey.set(key, period);
      (byEntity[name] = byEntity[name] || []).push(period);
    }
    for (const metric of metrics) {
      if (!(metric in row)) continue;
      hasMetric = true;
      if (!hasValue(row[metric])) {
        if (period[metric] === undefined) period[metric] = row[metric];
        continue;
      }
      if (hasValue(period[metric]) && period[metric] !== 0) {
        if (row[metric] === 0) continue;
        return null;
      }
      period[metric] = row[metric];
    }
  }
  if (!hasMetric) return null;
  Object.values(byEntity).forEach((periods) =>
    periods.sort(
      (a, b) => String(a.date).localeCompare(String(b.date)) || (a.qh_num || 0) - (b.qh_num || 0)
    )
  );
  return byEntity;
};
